import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';
import type { SimulateAgentMethod } from './user-account-api.service';

// ── Response shapes ───────────────────────────────────────────────────────────

/** PASS / REVIEW / FAIL outcome used across simulate scoring APIs. */
export type SimulateScoringResult = 'PASS' | 'REVIEW' | 'FAIL';

/** One document the agent's journey collects, keyed by registry code. */
export interface WorkflowDocument {
  code: string;
  key: string;
  name: string;
  hint: string;
  required: boolean;
  supplier: 'CUSTOMER_CAPTURE' | 'CUSTOMER_UPLOAD' | 'OPERATOR_UPLOAD';
}

export interface AgentRecord {
  /** Published flow this agent resolves to, or null when none matches its country + product. */
  workflow?: string | null;
  /** Flow explicitly pinned to this agent, or null when it resolves automatically. */
  flow_definition_id?: string | null;
  /** Everything the agent's workflow collects; drives the read-only Training → Documents tab. */
  workflow_documents?: WorkflowDocument[];
  id: number;
  uuid: string;
  agent_code: string | null;
  name: string;
  product_id: number;
  country_iso2: string | null;
  tenant_id: number | null;
  customer_type: string | null;
  customer_sub_profile: string | null;
  questions_by_profile: unknown;
  documents_by_profile: unknown;
  simulate_documents: unknown;
  review_acknowledged: boolean;
  status: 'DRAFT' | 'DEPLOYED' | 'ACTIVE' | 'INACTIVE';
  wizard_step: number;
  version: string;
  parent_agent_id: number | null;
  rule_package_version_id: string | null;
  deployed_at: string | null;
  trained_at: string | null;
  created_at: string;
  updated_at: string;
  total_pds: number;
  product?: { id: number; name: string; slug: string };
  tenant?: {
    id: number;
    uuid?: string | null;
    tenant_name: string | null;
    tenant_subdomain: string | null;
  };
}

export interface AgentListPayload {
  items: AgentRecord[];
  total: number;
  page: number;
  page_size: number;
}

export interface AgentPdSession {
  id: number;
  session_code: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  loan_type: string | null;
  status: string;
  result: string | null;
  score: number | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  /** Present for tenant-DB customer sessions; omit/null for control-plane simulate sessions. */
  tenant_id?: number | null;
}

export interface AgentSessionsPayload {
  items: AgentPdSession[];
  total: number;
  page: number;
  page_size: number;
}

export interface AgentListQuery {
  page?: number;
  page_size?: number;
  tab?: 'published' | 'draft' | 'deleted';
  tenant_id?: number;
  created_by?: number;
  status?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
}

export interface ActiveRulePackage {
  version_id: string;
  version_seq: number;
  country_code: string;
  configuration: unknown;
  activated_at: string;
}

// ── Payload shapes (mirror backend DTOs) ─────────────────────────────────────

export interface CreateAgentPayload {
  name: string;
  product_id: number;
  country_iso2: string;
  tenant_id?: number;
  customer_type?: string;
  customer_sub_profile?: string;
  /**
   * Explicit pin, or null to resolve automatically. Must be sent explicitly rather than omitted —
   * an omitted key leaves an existing pin untouched, so clearing one requires literal null.
   */
  flow_definition_id?: string | null;
}

export interface UpdateAgentDetailsPayload {
  name?: string;
  product_id?: number;
  country_iso2?: string;
  tenant_id?: number;
  customer_type?: string;
  customer_sub_profile?: string;
  /** See CreateAgentPayload.flow_definition_id — omitted leaves an existing pin untouched. */
  flow_definition_id?: string | null;
}

export interface SaveTrainingPayload {
  questions_by_profile: unknown;
  /** The Training step no longer edits documents — they come from the workflow now. */
  documents_by_profile?: unknown;
}

export interface SaveSimulatePayload {
  simulate_documents: unknown[];
}

export interface SaveEvaluatePayload {
  review_acknowledged: boolean;
}

export interface DeployAgentPayload {
  rule_package_version_id: string;
  tenant_id?: number;
}

export interface JourneySimulationAnswer {
  question_id: string;
  question: string;
  answer: string;
}

/** Raw workflow SCORING metadata sent when blending with interview simulate scoring. */
export interface WorkflowScoringSnapshot {
  score: number;
  maximum_score: number;
  result: SimulateScoringResult;
  score_completeness?: number;
}

export interface SimulateScoringBreakdown {
  method: 'average' | 'interview_only' | 'workflow_only';
  workflow?: { overall_score: number; result: SimulateScoringResult };
  interview?: { overall_score: number; result: SimulateScoringResult };
  weights?: { workflow: number; interview: number };
}

export interface RunJourneySimulationPayload {
  customer_name?: string;
  loan_amount?: number;
  loan_type?: string;
  geo_lat?: number;
  geo_lng?: number;
  geo_accuracy?: number;
  answers: JourneySimulationAnswer[];
  conversation_log?: Array<{ role: string; text: string; time: string }>;
  kyc_session_id?: number;
  /** ISO timestamp when the simulate interview started (runtime on reports). */
  session_started_at?: string;
  workflow_scoring?: WorkflowScoringSnapshot;
}

export interface IdentityValidationResult {
  declared_name: string | null;
  declared_dob: string | null;
  reference_name: string | null;
  reference_dob: string | null;
  name_score: number;
  dob_score: number;
  name_matched: boolean | null;
  dob_matched: boolean | null;
}

export interface JourneySimulationResult extends EvaluatePreviewScorePayload {
  customer_name?: string | null;
  identity_validation?: IdentityValidationResult | null;
  scoring_breakdown?: SimulateScoringBreakdown;
  /** Snapshot from simulate-journey, kept for re-submit or display. */
  workflow_snapshot?: WorkflowScoringSnapshot;
  session_id?: number | null;
}

export interface CloneAgentPayload {
  tenant_id: number;
  /** Override name for the clone; defaults to source agent name when omitted. */
  name?: string;
  /** Override customer type; inherits from source when omitted. */
  customer_type?: 'salaried' | 'self-employed' | 'self-employed-np';
  /** Override customer sub-profile; inherits from source when omitted. */
  customer_sub_profile?: 'bank' | 'cash';
  /** Copy training questions — default true. */
  copy_questions?: boolean;
  /** Copy training document metadata — default true. */
  copy_documents?: boolean;
  /** Copy simulate document metadata — default true. */
  copy_simulate?: boolean;
}

export type AgentVersionChangeType = 'MAJOR' | 'MINOR' | 'PATCH';

export interface CreateAgentVersionPayload {
  change_type: AgentVersionChangeType;
  notes?: string;
  /** MAJOR only: target tenant for the new agent; defaults to source tenant. */
  tenant_id?: number;
  /** MAJOR only: name override for the new agent. */
  name?: string;
}

/** An immutable version snapshot row (from GET /agent/:id/versions). */
export interface AgentVersionRecord {
  id: number;
  agent_id: number;
  version: string;
  change_type: AgentVersionChangeType;
  name: string;
  notes: string | null;
  rule_package_version_id: string | null;
  created_at: string;
  created_by: number;
}

export interface LiveKitVideoRoomResult {
  is_configured: boolean;
  livekit_url?: string;
  room_url?: string;
  room_name?: string;
  host_token?: string;
  guest_token?: string;
  participant_token?: string;
  expires_at?: number;
  video_provider?: 'livekit';
}

export interface KycVerifyPayload {
  /** Absent when the flow's capture step named no ID document; the face alone is then scored. */
  pan_capture?: string;
  selfie_capture: string;
  /** Base64 JPEG of Aadhaar back — captured for address extraction (PD product). */
  aadhaar_back_capture?: string;
  blink_count?: number;
  geo_lat?: number;
  geo_lng?: number;
  geo_accuracy?: number;
  conversation_log?: Array<{ role: string; text: string; time: string }>;
  session_started_at?: string;
}

export interface KycVerifyResult {
  composite_score: number;
  result: SimulateScoringResult;
  /** Null when the flow collects no PAN — an Aadhaar-only product has no PAN block. */
  pan: {
    captured_number: string | null;
    captured_name: string | null;
    date_of_birth: string | null;
    uploaded_number: string | null;
    uploaded_name?: string | null;
    uploaded_dob?: string | null;
    score: number;
    number_score?: number;
    name_score?: number;
    dob_score?: number;
    name_matched?: boolean;
    dob_matched?: boolean;
    matched: boolean;
    error: string | null;
  } | null;
  face: {
    score: number;
    matched: boolean;
    confidence: string;
    error: string | null;
    liveness_verified: boolean;
    liveness_score: number;
    liveness_error: string | null;
  };
  /** Address / identity from Aadhaar capture OCR — when the flow collected Aadhaar. */
  aadhaar_back?: {
    aadhaar_number?: string | null;
    name?: string | null;
    date_of_birth?: string | null;
    gender?: string | null;
    address: string | null;
    district: string | null;
    state: string | null;
    pincode: string | null;
    error: string | null;
  } | null;
}

/** Mirrors ai-service / super-admin AiClient `ScoringResult` + preview metadata. */
export interface EvaluateAnswerScore {
  question_id: number;
  question: string;
  answer: string;
  score: number;
  reasoning: string;
}

export interface EvaluatePreviewScoring {
  overall_score: number;
  result: SimulateScoringResult;
  answer_scores: EvaluateAnswerScore[];
  summary: string;
  flags: string[];
}

export interface EvaluatePreviewScorePayload {
  scoring: EvaluatePreviewScoring;
  question_count: number;
  used_agent_questions: boolean;
}

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AgentApiService {
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  private agentSeg(agentKey: string): string {
    return encodeURIComponent(agentKey.trim());
  }

  createAgent(body: CreateAgentPayload): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.post<ApiEnvelope<AgentRecord>>(`${this.base}/agent`, body);
  }

  updateDetails(agentKey: string, body: UpdateAgentDetailsPayload): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.patch<ApiEnvelope<AgentRecord>>(`${this.base}/agent/${this.agentSeg(agentKey)}`, body);
  }

  saveTraining(agentKey: string, body: SaveTrainingPayload): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.patch<ApiEnvelope<AgentRecord>>(`${this.base}/agent/${this.agentSeg(agentKey)}/training`, body);
  }

  /** Multipart upload — persists file (S3 when configured on API, else local disk) and updates `documents_by_profile`. */
  uploadTrainingDocument(
    agentKey: string,
    profile: string,
    rowIndex: number,
    file: File,
  ): Observable<ApiEnvelope<AgentRecord>> {
    const fd = new FormData();
    fd.set('profile', profile);
    fd.set('rowIndex', String(rowIndex));
    fd.set('file', file, file.name);
    return this.http.post<ApiEnvelope<AgentRecord>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/training-documents/upload`,
      fd,
    );
  }

  /** Same-origin URL for `<a download>` / new tab; cookie auth applies. */
  trainingDocumentDownloadUrl(agentKey: string, profile: string, rowIndex: number, inline = false): string {
    let p = new HttpParams().set('profile', profile).set('rowIndex', String(rowIndex));
    if (inline) {
      p = p.set('inline', '1');
    }
    return `${this.base}/agent/${this.agentSeg(agentKey)}/training-documents/download?${p.toString()}`;
  }

  /** Authenticated blob fetch for in-app preview (avoids forced download headers). */
  downloadTrainingDocumentBlob(
    agentKey: string,
    profile: string,
    rowIndex: number,
  ): Observable<Blob> {
    return this.http.get(this.trainingDocumentDownloadUrl(agentKey, profile, rowIndex, true), {
      responseType: 'blob',
    });
  }

  uploadSimulateDocument(
    agentKey: string,
    docKey: string,
    file: File,
    simulateAgentMethod?: SimulateAgentMethod,
  ): Observable<ApiEnvelope<AgentRecord>> {
    const fd = new FormData();
    fd.set('docKey', docKey);
    fd.set('file', file, file.name);
    if (simulateAgentMethod === 'ai' || simulateAgentMethod === 'idp') {
      fd.set('simulate_agent_method', simulateAgentMethod);
    }
    return this.http.post<ApiEnvelope<AgentRecord>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate-documents/upload`,
      fd,
    );
  }

  /** Removes the uploaded file; the row stays and returns to Pending. */
  deleteSimulateDocument(agentKey: string, docKey: string): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.delete<ApiEnvelope<AgentRecord>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate-documents/${encodeURIComponent(docKey)}`,
    );
  }

  simulateDocumentDownloadUrl(agentKey: string, docKey: string, inline = false): string {
    let p = new HttpParams().set('docKey', docKey);
    if (inline) {
      p = p.set('inline', '1');
    }
    return `${this.base}/agent/${this.agentSeg(agentKey)}/simulate-documents/download?${p.toString()}`;
  }

  /** Authenticated blob fetch for in-app preview (avoids forced download headers). */
  downloadSimulateDocumentBlob(agentKey: string, docKey: string): Observable<Blob> {
    return this.http.get(this.simulateDocumentDownloadUrl(agentKey, docKey, true), {
      responseType: 'blob',
    });
  }

  saveSimulate(agentKey: string, body: SaveSimulatePayload): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.patch<ApiEnvelope<AgentRecord>>(`${this.base}/agent/${this.agentSeg(agentKey)}/simulate`, body);
  }

  saveEvaluate(agentKey: string, body: SaveEvaluatePayload): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.patch<ApiEnvelope<AgentRecord>>(`${this.base}/agent/${this.agentSeg(agentKey)}/evaluate`, body);
  }

  /** Evaluate step — calls ai-service `POST /ai/score-answers` via super-admin (mock answers). */
  previewEvaluateMockScore(agentKey: string): Observable<ApiEnvelope<EvaluatePreviewScorePayload>> {
    return this.http.post<ApiEnvelope<EvaluatePreviewScorePayload>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/evaluate/preview-score`,
      {},
    );
  }

  /** Simulate step — scores admin-provided customer journey answers (real Q&A, not synthetic). */
  runJourneySimulation(agentKey: string, payload: RunJourneySimulationPayload): Observable<ApiEnvelope<JourneySimulationResult>> {
    return this.http.post<ApiEnvelope<JourneySimulationResult>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate/score-journey`,
      payload,
    );
  }

  /** Upload a local MediaRecorder video blob for a simulate session. */
  saveSimulateSessionRecording(
    sessionId: number,
    blob: Blob | null,
    conversationLog: Array<{ role: string; text: string; time: string }>,
  ): Observable<ApiEnvelope<unknown>> {
    const form = new FormData();
    if (blob) form.append('recording', blob, `session-${sessionId}-recording.webm`);
    form.append('conversation_log', JSON.stringify(conversationLog));
    return this.http.post<ApiEnvelope<unknown>>(
      `${this.base}/admin/sessions/${sessionId}/recording`,
      form,
    );
  }

  /** Simulate live interview — LiveKit room + participant token (customer role). */
  createSimulateVideoRoom(agentKey: string): Observable<ApiEnvelope<LiveKitVideoRoomResult>> {
    return this.http.post<ApiEnvelope<LiveKitVideoRoomResult>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate/video-room`,
      {},
    );
  }

  /** Simulate OTP — generates a 6-digit code and emails it to the logged-in admin. */
  sendSimulateOtp(agentKey: string): Observable<ApiEnvelope<{ email: string }>> {
    return this.http.post<ApiEnvelope<{ email: string }>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate/send-otp`,
      {},
    );
  }

  /** Check live camera crop is a PAN card (rejects Aadhaar / other IDs). */
  validatePanCapture(
    agentKey: string,
    payload: { image: string },
  ): Observable<ApiEnvelope<{ valid: boolean; message: string; pan_number: string | null }>> {
    return this.http.post<ApiEnvelope<{ valid: boolean; message: string; pan_number: string | null }>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate/validate-pan-capture`,
      payload,
    );
  }

  /** KYC simulate — sends camera captures of PAN card + selfie and returns composite score vs uploaded docs. */
  kycVerify(agentKey: string, payload: KycVerifyPayload): Observable<ApiEnvelope<KycVerifyResult>> {
    return this.http.post<ApiEnvelope<KycVerifyResult>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate/kyc-verify`,
      payload,
    );
  }

  /** OCR ground truth from uploaded simulate documents — load once when the interview Q&A phase starts. */
  getSimulateInterviewReference(agentKey: string): Observable<
    ApiEnvelope<{
      reference_context: string | null;
      document_refs: {
        pan_number?: string | null;
        name?: string | null;
        date_of_birth?: string | null;
        aadhaar_number?: string | null;
        address_text?: string | null;
      };
      has_document_reference: boolean;
    }>
  > {
    return this.http.get<
      ApiEnvelope<{
        reference_context: string | null;
        document_refs: Record<string, string | null | undefined>;
        has_document_reference: boolean;
      }>
    >(`${this.base}/agent/${this.agentSeg(agentKey)}/simulate/interview-reference`);
  }

  verifySimulateInterviewAnswer(
    agentKey: string,
    payload: {
      question: string;
      answer: string;
      expected_answer?: string;
      configured_followup?: string;
      followup_count?: number;
    },
  ): Observable<
    ApiEnvelope<{
      verdict: 'ok' | 'followup' | 'reject';
      followup_question: string | null;
      reason: string | null;
    }>
  > {
    return this.http.post<
      ApiEnvelope<{
        verdict: 'ok' | 'followup' | 'reject';
        followup_question: string | null;
        reason: string | null;
      }>
    >(`${this.base}/agent/${this.agentSeg(agentKey)}/simulate/verify-interview-answer`, payload);
  }

  /** Simulate OTP — verifies the 6-digit code the admin received by email. */
  verifySimulateOtp(agentKey: string, otp: string): Observable<ApiEnvelope<object>> {
    return this.http.post<ApiEnvelope<object>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate/verify-otp`,
      { otp },
    );
  }

  /** Simulate Digilocker Aadhaar — returns the popup URL and request id. */
  createSimulateAadhaarUrl(
    agentKey: string,
    body: { aadhaar_number: string; redirect_url: string },
  ): Observable<
    ApiEnvelope<{
      provider_mode: 'live' | 'mock';
      url: string;
      request_id: string;
      expiry_seconds: number;
      masked_aadhaar?: string;
    }>
  > {
    return this.http.post<
      ApiEnvelope<{
        provider_mode: 'live' | 'mock';
        url: string;
        request_id: string;
        expiry_seconds: number;
        masked_aadhaar?: string;
      }>
    >(`${this.base}/agent/${this.agentSeg(agentKey)}/simulate/aadhaar/create-url`, body);
  }

  /** Simulate Digilocker Aadhaar — fetches details after the popup. */
  fetchSimulateAadhaarDetails(
    agentKey: string,
  ): Observable<ApiEnvelope<{ name: string; dob: string; aadhaar_last4: string; provider_mode: 'live' | 'mock' }>> {
    return this.http.post<ApiEnvelope<{ name: string; dob: string; aadhaar_last4: string; provider_mode: 'live' | 'mock' }>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/simulate/aadhaar/details`,
      {},
    );
  }

  deployAgent(agentKey: string, body: DeployAgentPayload): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.post<ApiEnvelope<AgentRecord>>(`${this.base}/agent/${this.agentSeg(agentKey)}/deploy`, body);
  }

  listAgents(query: AgentListQuery = {}): Observable<ApiEnvelope<AgentListPayload>> {
    let p = new HttpParams();
    if (query.page) p = p.set('page', String(query.page));
    if (query.page_size) p = p.set('page_size', String(query.page_size));
    if (query.tab) p = p.set('tab', query.tab);
    if (query.tenant_id != null) p = p.set('tenant_id', String(query.tenant_id));
    if (query.created_by != null) p = p.set('created_by', String(query.created_by));
    if (query.status) p = p.set('status', query.status);
    if (query.search?.trim()) p = p.set('search', query.search.trim());
    if (query.date_from) p = p.set('date_from', query.date_from);
    if (query.date_to) p = p.set('date_to', query.date_to);
    return this.http.get<ApiEnvelope<AgentListPayload>>(`${this.base}/agent`, { params: p });
  }

  /** `agentKey` is the public `uuid` (preferred) or legacy numeric `id` as a string. */
  getAgentSessions(
    agentKey: string,
    params: { page?: number; page_size?: number; status?: string; result?: string } = {},
  ): Observable<ApiEnvelope<AgentSessionsPayload>> {
    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set('page', String(params.page));
    if (params.page_size) httpParams = httpParams.set('page_size', String(params.page_size));
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.result) httpParams = httpParams.set('result', params.result);
    return this.http.get<ApiEnvelope<AgentSessionsPayload>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/sessions`,
      { params: httpParams },
    );
  }

  getAgentById(agentKey: string): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.get<ApiEnvelope<AgentRecord>>(`${this.base}/agent/${this.agentSeg(agentKey)}`);
  }

  toggleStatus(agentKey: string): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.patch<ApiEnvelope<AgentRecord>>(`${this.base}/agent/${this.agentSeg(agentKey)}/toggle-status`, {});
  }

  deleteAgent(agentKey: string): Observable<ApiEnvelope<null>> {
    return this.http.delete<ApiEnvelope<null>>(`${this.base}/agent/${this.agentSeg(agentKey)}`);
  }

  restoreAgent(agentKey: string): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.patch<ApiEnvelope<AgentRecord>>(`${this.base}/agent/${this.agentSeg(agentKey)}/restore`, {});
  }

  permanentDeleteAgent(agentKey: string): Observable<ApiEnvelope<null>> {
    return this.http.delete<ApiEnvelope<null>>(
      `${this.base}/agent/permanent-delete/${this.agentSeg(agentKey)}`,
    );
  }

  /** Clone a deployed agent to another tenant — creates a ready-to-use DEPLOYED copy. */
  cloneAgent(agentKey: string, body: CloneAgentPayload): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.post<ApiEnvelope<AgentRecord>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/clone`,
      body,
    );
  }

  /** Publish a new version. MINOR/PATCH bump in place; MAJOR returns a NEW agent. */
  createAgentVersion(agentKey: string, body: CreateAgentVersionPayload): Observable<ApiEnvelope<AgentRecord>> {
    return this.http.post<ApiEnvelope<AgentRecord>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/version`,
      body,
    );
  }

  listAgentVersions(agentKey: string): Observable<ApiEnvelope<AgentVersionRecord[]>> {
    return this.http.get<ApiEnvelope<AgentVersionRecord[]>>(
      `${this.base}/agent/${this.agentSeg(agentKey)}/versions`,
    );
  }

  getActiveRulePackage(
    tenantId: number,
    productId: number,
    countryCode = 'INDIA',
  ): Observable<ApiEnvelope<ActiveRulePackage>> {
    return this.http.get<ApiEnvelope<ActiveRulePackage>>(
      `${this.base}/rules/tenant/${tenantId}/product/${productId}/country/${countryCode}/active-package`,
    );
  }
}
